import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import PriceLabel from '@prisjakt/ui-price-label';
import Rating from '@prisjakt/ui-rating';
import CartIcon from 'mdi-react/CartIcon';

import * as S from './styles';

const ProductRow = ({
  onClick,
  linkAttributes,
  link,
  image,
  name,
  rating,
  subtitle,
  campaignBadge,
  ratingCount,
  ratingCountTranslation,
  price,
  storesCount,
  storesCountText,
  children,
  className,
  showDivider,
  selectable,
  selected,
  onSelect,
  onDeselect,
}) => {
  const onToggle = selected ? onDeselect : onSelect;
  const clickHandler = useCallback(
    e => {
      if (selectable) {
        e.preventDefault();
        onToggle();
        return;
      }

      onClick(e);
    },
    [selectable, onToggle, onClick]
  );
  return (
    <S.Row
      className={className}
      showDivider={showDivider}
      selectable={selectable}
      selected={selected}
    >
      <S.ProductLink
        onClick={clickHandler}
        {...(linkAttributes || {})}
        to={link}
        aria-label={name}
      >
        {image && <S.ProductImage>{image}</S.ProductImage>}

        <S.BasicInfo>
          <S.ProductName>{name}</S.ProductName>

          {rating ? (
            <Rating
              hide={rating === null}
              rate={rating}
              showRate
              size="small"
              count={ratingCount}
              countTranslation={ratingCountTranslation}
              showCounter
            />
          ) : null}

          {subtitle && <S.Subtitle>{subtitle}</S.Subtitle>}
        </S.BasicInfo>

        {campaignBadge && <S.CampaignBadge>{campaignBadge}</S.CampaignBadge>}

        {price && (
          <S.PriceLabelWrapper>
            <PriceLabel value={price} />
            {storesCount !== null && (
              <S.StoreCount title={storesCountText}>
                <span>{storesCount}</span> <CartIcon height={16} />
              </S.StoreCount>
            )}
          </S.PriceLabelWrapper>
        )}
      </S.ProductLink>
      {children && <S.Gutter>{children}</S.Gutter>}
      {selectable && (
        <S.SelectBox
          selected={selected}
          onSelect={onSelect}
          onDeselect={onDeselect}
          aria-label={name}
        />
      )}
    </S.Row>
  );
};

const {
  func,
  string,
  number,
  oneOfType,
  element,
  bool,
  node,
  shape,
} = PropTypes;

ProductRow.propTypes = {
  onClick: func,
  link: string,
  image: node,
  name: string.isRequired,
  rating: number,
  ratingCount: number,
  ratingCountTranslation: shape({
    domain: string,
    key: string,
  }),
  subtitle: node,
  campaignBadge: element,
  price: oneOfType([string, number, element]),
  linkAttributes: shape({
    target: string,
    rel: string,
  }),
  storesCount: number,
  storesCountText: string,
  children: node,
  className: string,
  showDivider: bool,
  selectable: bool,
  selected: bool,
  onSelect: func,
  onDeselect: func,
};

const noop = () => {};

ProductRow.defaultProps = {
  onClick: noop,
  image: null,
  rating: undefined,
  ratingCount: undefined,
  ratingCountTranslation: {
    domain: 'product',
    key: 'product-card.n-ratings',
  },
  subtitle: undefined,
  campaignBadge: null,
  price: undefined,
  linkAttributes: undefined,
  storesCount: null,
  storesCountText: '',
  children: null,
  className: undefined,
  showDivider: false,
  selectable: false,
  selected: false,
  onSelect: noop,
  onDeselect: noop,
  link: undefined,
};

export default ProductRow;
